/**
 * The BAM intern standard, as numbers (Tyler Clark's proposal, the DETAIL
 * San Diego program made the network standard). Pure, so the hub, the
 * roster and the tests all agree.
 *
 *   Observation 15h → Assisted Coaching 40h → Lead Trainer 45h = 100h
 *   ~90 days is the target pace, not a deadline.
 *   Every week: 1 drill upload and 1 community post.
 */
import { CERT_UNITS } from "../certification/constants";

export const STAGES = [
  { key: "observation",  label: "Observation",       hours: 15 },
  { key: "assisted",     label: "Assisted Coaching", hours: 40 },
  { key: "lead_trainer", label: "Lead Trainer",      hours: 45 },
];
export const TOTAL_HOURS = STAGES.reduce((n, s) => n + s.hours, 0);
export const TARGET_DAYS = 90;
export const STAGE_LABEL = Object.fromEntries([...STAGES.map(s => [s.key, s.label]), ["completed", "Completed"]]);

/** Minutes between two "HH:MM" times; a time out before the time in is the next day. */
export function minutesBetween(timeIn, timeOut) {
  const m = (t) => { const [h, mm] = String(t || "").split(":").map(Number); return Number.isFinite(h) && Number.isFinite(mm) ? h * 60 + mm : NaN; };
  const a = m(timeIn), b = m(timeOut);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return b > a ? b - a : b + 1440 - a;
}

export const fmtHours = (minutes) => {
  const h = (minutes || 0) / 60;
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
};

/** Day N of the internship, counting the start date as day 1. */
export function dayOf(startedOn, now = new Date()) {
  if (!startedOn) return 1;
  const start = new Date(`${startedOn}T00:00:00`);
  return Math.max(1, Math.floor((now - start) / 86400000) + 1);
}

/** Verified minutes per stage, from a roster row. */
export function stageMinutes(row) {
  return { observation: row?.obs_minutes || 0, assisted: row?.assisted_minutes || 0, lead_trainer: row?.lead_minutes || 0 };
}

export function verifiedMinutes(row) {
  const s = stageMinutes(row);
  return s.observation + s.assisted + s.lead_trainer;
}

/**
 * On pace means verified plus pending hours are at least 80% of what an
 * even 90-day pace would have by today. A guide for the lead, not a grade.
 */
export function paceStatus(row, now = new Date()) {
  if (!row || row.status !== "active") return { key: row?.status || "inactive", label: STATUS_LABEL[row?.status] || "Inactive" };
  const day = dayOf(row.started_on, now);
  const expected = Math.min(TOTAL_HOURS, (day / TARGET_DAYS) * TOTAL_HOURS) * 60;
  const have = verifiedMinutes(row) + (row.pending_minutes || 0);
  if (verifiedMinutes(row) >= TOTAL_HOURS * 60) return { key: "done", label: "Hours complete" };
  return have >= expected * 0.8 || day <= 7 ? { key: "on", label: "On pace" } : { key: "behind", label: "Behind" };
}
export const STATUS_LABEL = { active: "Active", paused: "Paused", completed: "Completed", exited: "Exited" };

/** This week's two requirements: met by activity or a lead's mark. */
export function weekStatus(row) {
  const one = (count, mark) => mark === "excused" ? "excused" : mark === "missed" ? "missed" : (mark === "met" || count > 0) ? "met" : "open";
  return { drill: one(row?.week_drills || 0, row?.mark_drill), post: one(row?.week_posts || 0, row?.mark_post) };
}

/** Share of Certification lessons done, 0 to 100, from cert_progress. */
export function certPercent(progress) {
  const p = progress && typeof progress === "object" ? progress : {};
  const lessons = CERT_UNITS.flatMap(u => u.lessons || []);
  if (!lessons.length) return 0;
  const done = lessons.filter(l => !!p[l.id]).length;
  return Math.round((done / lessons.length) * 100);
}

/** Monday of this week (UTC), as YYYY-MM-DD: the same week the roster counts. */
export function weekStartUTC(now = new Date()) {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const back = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - back);
  return d.toISOString().slice(0, 10);
}

/**
 * Where an intern is in the 15 / 40 / 45 journey. `ready` means the current
 * stage's hours are verified, so the lead can move them up.
 */
export function stageProgress(row) {
  const per = stageMinutes(row);
  const i = STAGES.findIndex(s => s.key === row?.stage);
  if (i < 0) return { current: null, next: null, minutesLeft: 0, ready: false };
  const cur = STAGES[i];
  const left = Math.max(0, cur.hours * 60 - per[cur.key]);
  const next = STAGES[i + 1] || null;
  return { current: cur, next, minutesLeft: left, ready: left === 0 };
}

/** The first Certification lesson not done yet, with its unit, or null when finished. */
export function nextCertLesson(progress) {
  const p = progress && typeof progress === "object" ? progress : {};
  for (const u of CERT_UNITS) {
    for (const l of u.lessons || []) if (!p[l.id]) return { unitId: u.id, lessonId: l.id, title: l.title, unitTitle: u.title };
  }
  return null;
}

/**
 * The one thing an intern should do next, most urgent first: fix an entry
 * their lead sent back, then this week's post and drill, then log a session
 * if nothing's in this week, then the next Certification lesson.
 */
export function internNextUp(row, hours = [], now = new Date()) {
  const back = hours.find(h => h.status === "rejected" && !h._resent);
  if (back) return { kind: "fix", entry: back, text: `${back.session_name} was sent back. Fix it and resend.` };
  const week = weekStatus(row);
  if (week.post === "open") return { kind: "post", text: "Community post due Sunday." };
  if (week.drill === "open") return { kind: "drill", text: "Drill upload due Sunday." };
  const ws = weekStartUTC(now);
  if (!hours.some(h => h.session_date >= ws && h.status !== "rejected")) return { kind: "log", text: "No sessions logged this week yet." };
  const lesson = nextCertLesson(row?.cert_progress);
  if (lesson) return { kind: "cert", lesson, text: `Next in the Certification: ${lesson.title}.` };
  return { kind: "done", text: "You're all caught up this week." };
}

/**
 * Roster order for a lead: whatever needs them first (hours or a solo
 * session to verify, a stage to move), then behind, then on pace, then
 * paused, completed and exited.
 */
export function attentionRank(row, now = new Date()) {
  if (row.status === "exited") return 6;
  if (row.status === "completed") return 5;
  if (row.status === "paused") return 4;
  if (row.pending_count > 0 || stageProgress(row).ready) return 0;
  return paceStatus(row, now).key === "behind" ? 1 : 2;
}
